import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import MuxPlayer from '@mux/mux-player-react';
import { Clock, Share2, Lock, Play, Mail, ExternalLink, MessageCircle, X, Tv } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, API } from '@/api';
import { SEO } from '@/components/common/SEO';
import { resolveMediaUrl, formatDate, formatTime, getYouTubeEmbedUrl } from '@/utils/helpers';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';
import { useTVMode } from '@/contexts/TVModeContext';
import ChatPanel from '@/components/common/ChatPanel';
import useViewTracking from '@/hooks/useViewTracking';
import { DeviceLimitModal } from '@/components/common/DeviceLimitModal';
import { CommercialIPModal } from '@/components/common/CommercialIPModal';
import { TVSignIn } from '@/components/common/TVSignIn';
import { getDeviceFingerprint } from '@/utils/deviceFingerprint';

// Countdown hook
const useCountdown = (targetDate) => {
  const [remaining, setRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, passed: false });
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) { setRemaining(r => ({ ...r, passed: true })); return; }
      setRemaining({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        passed: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return remaining;
};

const GameWatchPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { viewer } = useViewerAuth();
  const { tvMode } = useTVMode();
  const videoContainerRef = useRef(null);
  const [watchData, setWatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [relatedGames, setRelatedGames] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [plans, setPlans] = useState([]);
  const [viewStats, setViewStats] = useState(null);
  const [playbackToken, setPlaybackToken] = useState(null);

  // Device enforcement state
  const [deviceLimitSessions, setDeviceLimitSessions] = useState(null);
  const [commercialIPData, setCommercialIPData] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [showTVSignIn, setShowTVSignIn] = useState(false);

  // Track view analytics
  const isLive = watchData?.stream_type === 'live' || watchData?.mux_stream_status === 'active';
  useViewTracking(gameId, isLive);

  // Auto-fullscreen video in TV mode
  useEffect(() => {
    if (!tvMode || loading || error || !watchData) return;
    const el = videoContainerRef.current;
    if (!el) return;
    const timer = setTimeout(() => { el.classList.add('tv-video-fullscreen'); }, 1500);
    const handleEsc = (e) => {
      if (e.key === 'Escape' || e.key === 'Backspace') el.classList.remove('tv-video-fullscreen');
    };
    window.addEventListener('keydown', handleEsc);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', handleEsc); };
  }, [tvMode, loading, error, watchData]);

  useEffect(() => {
    const load = async () => {
      try {
        const [watchRes, plansRes] = await Promise.all([
          api.getWatchData(gameId),
          api.getPlans(),
        ]);
        setWatchData(watchRes.data);
        setPlans(plansRes.data.plans || []);

        // Load related games from same league
        try {
          const gamesRes = await api.getGames({ limit: 8 });
          const related = (gamesRes.data || []).filter(g => g.id !== gameId);
          setRelatedGames(related.slice(0, 8));
        } catch { /* silent */ }
      } catch (err) {
        setError(err.response?.status === 404 ? 'Game not found' : 'Failed to load game');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [gameId]);

  // Check access
  useEffect(() => {
    if (!watchData) return;
    if (watchData.is_free || watchData.access_type === 'free') {
      setHasAccess(true);
      return;
    }
    const check = async () => {
      try {
        const res = await api.checkGameAccess(gameId);
        setHasAccess(res.data.has_access);
      } catch { setHasAccess(false); }
    };
    check();
  }, [gameId, watchData, viewer]);

  // Fetch signed playback token for paid games when access is granted
  useEffect(() => {
    if (!watchData || !hasAccess) return;
    if (watchData.needs_signed_token && !playbackToken) {
      const fetchToken = async () => {
        try {
          const res = await api.getPlaybackToken(gameId);
          setPlaybackToken(res.data);
        } catch { /* token fetch failed — player will show error */ }
      };
      fetchToken();
    }
  }, [gameId, watchData, hasAccess, playbackToken]);

  // Device session heartbeat — register this device and check concurrent limit
  useEffect(() => {
    if (!viewer || !hasAccess || !watchData) return;
    // Initialize fingerprint cookie
    getDeviceFingerprint();

    const doHeartbeat = async () => {
      try {
        await api.sessionHeartbeat();
        setSessionActive(true);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail?.code === 'device_limit') {
          setDeviceLimitSessions(detail.active_sessions);
        } else if (detail?.code === 'account_banned' || detail?.code === 'ip_banned') {
          setCommercialIPData({ banned: true, message: detail.message });
        }
      }
    };
    doHeartbeat();
    // Refresh heartbeat every 60 seconds
    const interval = setInterval(doHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [viewer, hasAccess, watchData]);

  // Commercial IP check — run once when page loads with a viewer
  useEffect(() => {
    if (!viewer || !watchData) return;
    const checkIP = async () => {
      try {
        const res = await api.checkCommercialIP();
        if (res.data.is_commercial && !res.data.has_valid_pass && res.data.blocked) {
          setCommercialIPData(res.data);
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail?.code === 'account_banned' || detail?.code === 'ip_banned') {
          setCommercialIPData({ banned: true, message: detail.message });
        }
      }
    };
    checkIP();
  }, [viewer, watchData]);

  // Handle payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    setProcessingPayment(true);
    const poll = async () => {
      for (let i = 0; i < 10; i++) {
        try {
          const res = await api.checkPaymentStatus(sessionId);
          if (res.data.payment_status === 'paid') { setHasAccess(true); setProcessingPayment(false); return; }
        } catch { /* continue */ }
        await new Promise(r => setTimeout(r, 2000));
      }
      setProcessingPayment(false);
    };
    poll();
  }, [searchParams]);

  // Poll for live status
  useEffect(() => {
    if (!watchData?.mux_playback_id) return;
    const interval = setInterval(async () => {
      try { const res = await api.getWatchData(gameId); setWatchData(res.data); } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(interval);
  }, [gameId, watchData?.mux_playback_id]);

  // Fetch public view stats
  useEffect(() => {
    if (!gameId) return;
    const fetchStats = async () => {
      try { const res = await api.getPublicGameStats(gameId); setViewStats(res.data); } catch { /* silent */ }
    };
    fetchStats();
    const statsInterval = setInterval(fetchStats, 30000);
    return () => clearInterval(statsInterval);
  }, [gameId]);

  const handleShare = async () => {
    const shareUrl = `${API}/share/watch/${gameId}`;
    try { await navigator.clipboard.writeText(shareUrl); } catch {
      const i = document.createElement('input'); i.value = shareUrl; document.body.appendChild(i); i.select(); document.execCommand('copy'); document.body.removeChild(i);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubscribe = (planId) => {
    if (!viewer) { navigate(`/signup?plan=${planId || 'annual'}&game=${gameId}&return=/watch/${gameId}`); return; }
    api.subscriptionCheckout({ plan_id: planId || 'annual', origin_url: window.location.origin })
      .then(res => { window.location.href = res.data.url; })
      .catch(() => {});
  };

  const handlePPV = () => {
    if (!viewer) { navigate(`/signup?game=${gameId}&return=/watch/${gameId}`); return; }
    api.ppvCheckout({ game_id: gameId, origin_url: window.location.origin })
      .then(res => { window.location.href = res.data.url; })
      .catch(() => {});
  };

  // Temporarily redirect subscription/PPV games to NLS+ site
  const showPaywall = watchData && watchData.access_type !== 'free' && !watchData.is_free && hasAccess === false && !processingPayment;
  useEffect(() => {
    if (showPaywall) {
      window.location.href = 'https://plus.newlogicalsports.com';
    }
  }, [showPaywall]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !watchData) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-2">{error || 'Game not found'}</h1>
        <Link to="/" className="text-sky-400 hover:text-sky-300">Back to Home</Link>
      </div>
    </div>
  );

  const { home_team_name, away_team_name, home_team_logo, away_team_logo, league_name, scheduled_time, mux_playback_id, stream_type, is_free, ppv_price, access_type, thumbnail, home_score, away_score, mux_stream_status, video_sources, replay_url, needs_signed_token } = watchData;
  const title = `${home_team_name} vs ${away_team_name}`;
  const canPlay = (hasAccess || is_free || access_type === 'free') && !showPaywall;

  // Resolve the effective playback ID: use signed token when available, else raw ID
  const effectivePlaybackId = playbackToken?.playback_id || mux_playback_id;
  const muxTokens = playbackToken?.token ? { playback: playbackToken.token } : undefined;

  // Format view count
  const formatViews = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  // Determine video source
  const youtubeSource = video_sources?.find(s => s.type === 'youtube');
  const externalSource = video_sources?.find(s => s.type === 'external');
  const hasVideo = effectivePlaybackId || youtubeSource || externalSource || replay_url;

  return (
    <div className="min-h-screen bg-gray-950" data-testid="game-watch-page">
      <SEO title={`${title} | NLS`} description={`Watch ${title} on New Logical Sports`} />

      {/* Device Limit Modal */}
      {deviceLimitSessions && (
        <DeviceLimitModal
          sessions={deviceLimitSessions}
          maxDevices={2}
          onSessionRevoked={(id) => {
            setDeviceLimitSessions(prev => prev.filter(s => s.id !== id));
            if (deviceLimitSessions.length <= 2) {
              setDeviceLimitSessions(null);
              // Re-attempt heartbeat
              api.sessionHeartbeat().then(() => setSessionActive(true)).catch(() => {});
            }
          }}
          onClose={() => setDeviceLimitSessions(null)}
        />
      )}

      {/* Commercial IP Enforcement Modal */}
      {commercialIPData && (
        <CommercialIPModal
          data={commercialIPData}
          onClose={() => setCommercialIPData(null)}
        />
      )}

      {/* Main Content: YouTube-style 2-column layout */}
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* LEFT: Video Player */}
          <div className="flex-1 min-w-0">
            <div ref={videoContainerRef} className="relative aspect-video w-full bg-black rounded-xl overflow-hidden">
              <VideoArea
                canPlay={canPlay}
                isLive={isLive}
                processingPayment={processingPayment}
                muxPlaybackId={effectivePlaybackId}
                muxTokens={muxTokens}
                youtubeSource={youtubeSource}
                externalSource={externalSource}
                replayUrl={replay_url}
                thumbnail={thumbnail}
                scheduledTime={scheduled_time}
                gameId={gameId}
                title={title}
                streamType={stream_type}
                showPaywall={showPaywall}
                homeTeamLogo={home_team_logo}
                awayTeamLogo={away_team_logo}
                homeTeamName={home_team_name}
                awayTeamName={away_team_name}
              />

              {/* LIVE badge */}
              {isLive && canPlay && (
                <div className="absolute top-3 left-3 z-20">
                  <Badge className="bg-red-600 text-white text-sm px-3 py-1 animate-pulse" data-testid="live-badge">
                    <span className="w-2 h-2 bg-white rounded-full inline-block mr-2 animate-ping" /> LIVE
                  </Badge>
                </div>
              )}
            </div>

            {/* Below player: Title bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-white text-xl font-bold" data-testid="game-title">
                    {home_team_name} vs. {away_team_name}
                  </h1>
                  <p className="text-gray-400 text-sm mt-0.5">
                    {league_name} &middot; {formatDate(scheduled_time)}
                    {viewStats && (
                      <span className="ml-2 text-gray-500" data-testid="view-count">
                        &middot; {formatViews(viewStats.total_views)} view{viewStats.total_views !== 1 ? 's' : ''}
                        {viewStats.current_viewers > 0 && (
                          <span className="text-red-400 ml-1">({viewStats.current_viewers} watching now)</span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {home_score !== null && away_score !== null && (
                    <div className="bg-gray-800 px-4 py-1.5 rounded-lg mr-2">
                      <span className="text-xl font-bold text-white">{home_score} - {away_score}</span>
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={handleShare} className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="share-btn">
                    <Share2 className="w-4 h-4 mr-1" /> {copied ? 'Copied!' : 'Share'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowChat(!showChat)} className="border-gray-700 text-gray-300 hover:bg-gray-800 lg:hidden" data-testid="chat-toggle">
                    <MessageCircle className="w-4 h-4 mr-1" /> Chat
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Info Sidebar */}
          <div className="w-full lg:w-[380px] xl:w-[420px] shrink-0 space-y-4">
            {/* Paywall Buttons */}
            {showPaywall && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4" data-testid="paywall-sidebar">
                <div className="flex items-center gap-3 mb-2">
                  <Lock className="w-5 h-5 text-sky-400" />
                  <span className="text-white font-semibold">This game requires a subscription</span>
                </div>

                {/* Subscribe button */}
                <Button
                  onClick={() => handleSubscribe(null)}
                  className="w-full bg-sky-400 hover:bg-sky-500 text-gray-950 font-bold py-6 text-base rounded-lg"
                  data-testid="subscribe-btn"
                >
                  Subscribe to watch
                </Button>

                {/* PPV button */}
                {(ppv_price || access_type === 'ppv') && (
                  <Button
                    onClick={handlePPV}
                    variant="outline"
                    className="w-full border-gray-600 text-white hover:bg-gray-800 py-5 text-base rounded-lg"
                    data-testid="ppv-btn"
                  >
                    One-time purchase of ${ppv_price?.toFixed(2) || '—'}
                  </Button>
                )}

                {!viewer && (
                  <div className="space-y-3">
                    <p className="text-gray-500 text-sm text-center">
                      Already a member?{' '}
                      <Link to={`/signup?return=/watch/${gameId}`} className="text-sky-400 hover:text-sky-300">Sign In</Link>
                    </p>
                    <button
                      onClick={() => setShowTVSignIn(!showTVSignIn)}
                      className="w-full text-sky-400 hover:text-sky-300 text-sm py-2 flex items-center justify-center gap-2 border border-sky-500/20 rounded-lg hover:bg-sky-500/5 transition-colors"
                      data-testid="game-tv-signin-trigger"
                    >
                      <Tv className="w-4 h-4" /> Sign in with TV code
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TV Sign-In Panel */}
            {showTVSignIn && !viewer && (
              <div className="bg-gray-900 border border-sky-500/30 rounded-xl overflow-hidden" data-testid="game-tv-signin-overlay">
                <div className="flex items-center justify-between px-4 pt-3">
                  <span className="text-xs text-sky-400 font-semibold uppercase tracking-widest">TV Sign In</span>
                  <button onClick={() => setShowTVSignIn(false)} className="text-gray-500 hover:text-white text-xs">Close</button>
                </div>
                <TVSignIn onSignedIn={() => {
                  setShowTVSignIn(false);
                  window.location.reload();
                }} />
              </div>
            )}

            {/* Game Details Card */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="game-details">
              {/* Team logos and names */}
              <div className="flex items-center justify-center gap-6 mb-4">
                <div className="text-center">
                  {home_team_logo ? (
                    <img src={resolveMediaUrl(home_team_logo)} alt="" className="w-16 h-16 mx-auto rounded-full bg-white/10 object-cover" />
                  ) : (
                    <div className="w-16 h-16 mx-auto rounded-full bg-gray-700 flex items-center justify-center text-white font-bold text-lg">{home_team_name?.[0]}</div>
                  )}
                  <p className="text-white text-sm font-medium mt-2">{home_team_name}</p>
                </div>
                <span className="text-gray-500 text-lg font-medium">vs</span>
                <div className="text-center">
                  {away_team_logo ? (
                    <img src={resolveMediaUrl(away_team_logo)} alt="" className="w-16 h-16 mx-auto rounded-full bg-white/10 object-cover" />
                  ) : (
                    <div className="w-16 h-16 mx-auto rounded-full bg-gray-700 flex items-center justify-center text-white font-bold text-lg">{away_team_name?.[0]}</div>
                  )}
                  <p className="text-white text-sm font-medium mt-2">{away_team_name}</p>
                </div>
              </div>

              <div className="border-t border-gray-800 pt-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">League</span>
                  <span className="text-white">{league_name}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white">{formatDate(scheduled_time)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Time</span>
                  <span className="text-white">{formatTime(scheduled_time)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Status</span>
                  <Badge className={isLive ? 'bg-red-500 text-white' : 'bg-gray-700 text-gray-300'}>
                    {isLive ? 'LIVE' : watchData.status === 'finished' ? 'Replay' : 'Upcoming'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Chat Panel (desktop) */}
            <div className="hidden lg:block bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" style={{ height: '360px' }}>
              <ChatPanel gameId={gameId} />
            </div>
          </div>
        </div>

        {/* Mobile Chat Panel */}
        {showChat && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowChat(false)} />
            <div className="absolute bottom-0 left-0 right-0 h-[70vh] bg-gray-900 rounded-t-2xl overflow-hidden">
              <div className="flex items-center justify-between p-3 border-b border-gray-800">
                <span className="text-white font-semibold text-sm">Live Chat</span>
                <button onClick={() => setShowChat(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <ChatPanel gameId={gameId} />
            </div>
          </div>
        )}

        {/* Related Videos */}
        {relatedGames.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-white mb-4" data-testid="related-heading">Related Videos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {relatedGames.map(game => (
                <Link
                  key={game.id}
                  to={`/watch/${game.id}`}
                  className="group rounded-xl overflow-hidden bg-gray-900/50 hover:bg-gray-800/50 transition-colors"
                  data-testid={`related-${game.id}`}
                >
                  <div className="relative aspect-video bg-gray-800">
                    {game.thumbnail ? (
                      <img src={resolveMediaUrl(game.thumbnail)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <Play className="w-8 h-8" />
                      </div>
                    )}
                    {game.status === 'live' && (
                      <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs">LIVE</Badge>
                    )}
                    {game.access_type !== 'free' && game.is_paywall && (
                      <div className="absolute top-2 right-2"><Lock className="w-3.5 h-3.5 text-white/70" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm font-medium truncate group-hover:text-sky-300 transition-colors">
                      {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
                    </p>
                    <p className="text-gray-500 text-xs mt-1 truncate">{game.league_name} &middot; {formatDate(game.scheduled_time)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Extracted video area component
const VideoArea = ({ canPlay, isLive, processingPayment, muxPlaybackId, muxTokens, youtubeSource, externalSource, replayUrl, thumbnail, scheduledTime, gameId, title, streamType, showPaywall, homeTeamLogo, awayTeamLogo, homeTeamName, awayTeamName }) => {
  const countdown = useCountdown(scheduledTime);

  if (processingPayment) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-white font-medium">Processing your payment...</p>
        </div>
      </div>
    );
  }

  // Paywall overlay — blurred thumbnail with team crests and countdown
  if (showPaywall) {
    return (
      <div className="absolute inset-0 z-20">
        {thumbnail && <img src={resolveMediaUrl(thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover blur-sm" />}
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          {/* Team crests */}
          <div className="flex items-center gap-6 mb-6">
            {homeTeamLogo ? (
              <img src={resolveMediaUrl(homeTeamLogo)} alt="" className="w-20 h-20 rounded-full bg-white/10 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">{homeTeamName?.[0]}</div>
            )}
            <span className="text-gray-300 text-2xl font-light">vs</span>
            {awayTeamLogo ? (
              <img src={resolveMediaUrl(awayTeamLogo)} alt="" className="w-20 h-20 rounded-full bg-white/10 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">{awayTeamName?.[0]}</div>
            )}
          </div>

          {/* Countdown */}
          {!countdown.passed && (
            <div className="flex gap-4 mb-4">
              {[
                { val: countdown.days, label: 'DAYS' },
                { val: countdown.hours, label: 'HOURS' },
                { val: countdown.minutes, label: 'MINUTES' },
                { val: countdown.seconds, label: 'SECONDS' },
              ].map(({ val, label }) => (
                <div key={label} className="text-center">
                  <div className="text-white text-3xl sm:text-4xl font-bold tabular-nums">{String(val).padStart(2, '0')}</div>
                  <div className="text-gray-400 text-xs mt-1 tracking-wider">{label}</div>
                </div>
              ))}
            </div>
          )}

          <p className="text-sky-300 text-sm font-medium tracking-wide uppercase">Watch on New Logical Sports</p>
        </div>
      </div>
    );
  }

  // Player: Mux
  if (canPlay && muxPlaybackId) {
    return (
      <MuxPlayer
        playbackId={muxPlaybackId}
        {...(muxTokens ? { tokens: muxTokens } : {})}
        streamType={isLive ? 'live' : 'on-demand'}
        autoPlay="muted"
        metadata={{ video_id: gameId, video_title: title }}
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        data-testid="mux-player"
      />
    );
  }

  // Player: YouTube
  if (canPlay && youtubeSource) {
    const embedUrl = getYouTubeEmbedUrl(youtubeSource.url);
    if (embedUrl) {
      return (
        <iframe
          src={embedUrl + '?autoplay=1'}
          allow="autoplay; encrypted-media"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          data-testid="youtube-player"
        />
      );
    }
  }

  // Player: External iframe
  if (canPlay && externalSource) {
    return (
      <iframe src={externalSource.url} allow="autoplay; encrypted-media" allowFullScreen className="absolute inset-0 w-full h-full" />
    );
  }

  // Player: Replay URL
  if (canPlay && replayUrl) {
    const embedUrl = getYouTubeEmbedUrl(replayUrl);
    if (embedUrl) {
      return <iframe src={embedUrl + '?autoplay=1'} allow="autoplay; encrypted-media" allowFullScreen className="absolute inset-0 w-full h-full" />;
    }
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
        <a href={replayUrl} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 flex items-center gap-2">
          <ExternalLink className="w-5 h-5" /> Watch Replay
        </a>
      </div>
    );
  }

  // Waiting / No video — show countdown with team crests
  return (
    <div className="absolute inset-0" style={{ backgroundColor: '#0f1923' }}>
      {thumbnail && (
        <>
          <img src={resolveMediaUrl(thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/60" />
        </>
      )}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
        <div className="flex items-center gap-6 mb-6">
          {homeTeamLogo ? (
            <img src={resolveMediaUrl(homeTeamLogo)} alt="" className="w-16 h-16 rounded-full bg-white/10 object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">{homeTeamName?.[0]}</div>
          )}
          <span className="text-gray-400 text-xl">vs</span>
          {awayTeamLogo ? (
            <img src={resolveMediaUrl(awayTeamLogo)} alt="" className="w-16 h-16 rounded-full bg-white/10 object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">{awayTeamName?.[0]}</div>
          )}
        </div>

        {!countdown.passed ? (
          <div className="flex gap-4 mb-3">
            {[
              { val: countdown.days, label: 'DAYS' },
              { val: countdown.hours, label: 'HOURS' },
              { val: countdown.minutes, label: 'MINUTES' },
              { val: countdown.seconds, label: 'SECONDS' },
            ].map(({ val, label }) => (
              <div key={label} className="text-center">
                <div className="text-white text-2xl sm:text-3xl font-bold tabular-nums">{String(val).padStart(2, '0')}</div>
                <div className="text-gray-500 text-xs mt-1 tracking-wider">{label}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/80 text-lg font-semibold mb-2">Stream Starting Soon</p>
        )}
        <p className="text-gray-400 text-sm flex items-center gap-1">
          <Clock className="w-4 h-4" /> {formatDate(scheduledTime)} at {formatTime(scheduledTime)}
        </p>
      </div>
    </div>
  );
};

export default GameWatchPage;
